import {
  Box,
  Stack,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterialColor, useMaterials } from "hooks/useMaterials";
import { useState } from "react";
import { EditableHotkey } from "../EditableHotkey";
import { MaterialExampleVideoPlayer } from "../MaterialExampleVideoPlayer";
import { getModifierKey } from "hooks/usePlatform";

export const MaterialGuide = () => {
  const { t } = useTenantTranslation();
  const { materials } = useMaterials();
  const { getColor } = useMaterialColor();
  const { shift } = getModifierKey();
  const [selectedMaterial, setSelectedMaterial] = useState<string | null>(null);

  const visibleMaterials =
    materials?.filter((material) => material.labelling_name !== null) ?? [];

  if (selectedMaterial) {
    return (
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setSelectedMaterial(null);
            }}
          >
            {t("backToMaterialList")}
          </Button>
          <Typography variant="h6">
            {
              visibleMaterials.find(
                (material) => material.name == selectedMaterial
              )?.labelling_name
            }
          </Typography>
        </Stack>
        <MaterialExampleVideoPlayer materialName={selectedMaterial} />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h6">{t("materialGuideTitle")}</Typography>

      <Typography variant="body1">{t("materialGuideDescription")}</Typography>

      <Alert severity="info" icon={<InfoIcon />}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          {t("usingShiftToSetMaterial")}
        </Typography>
        <Typography variant="body2">
          {t("shiftMaterialDescription", { shift })}
        </Typography>
      </Alert>

      <Grid container spacing={2}>
        {visibleMaterials.map((material, index) => (
          <Card
            key={material.name}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              border: 1,
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                height: 8,
                bgcolor: getColor(material.name) || "grey.400",
              }}
            />
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                {material.labelling_name}
              </Typography>

              <Stack direction="row" spacing={0.5} alignItems="center" mb={1}>
                <Typography variant="caption">{t("hotkeyLabel")}:</Typography>
                <EditableHotkey hotkeyId={`material_${index}`} />
              </Stack>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                onClick={() => {
                  setSelectedMaterial(material.name);
                }}
                fullWidth
              >
                {t("viewExample")}
              </Button>
            </CardActions>
          </Card>
        ))}
      </Grid>

      {visibleMaterials.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t("noMaterialsConfigured")}
        </Typography>
      )}
    </Stack>
  );
};
